import { Utils } from './Utils';

export interface StockReportRow {
	categoria: string;
	articulo: string;
	codigo: string;
	sucursal: string;
	cantidad: number;
}

/**
 * Display-ready stock report data.
 *
 * Mirrors the rows printed by StockSearchReportComponent.print
 * (src/app/pages/stock-search-report): one row per item and store with the
 * raw quantity. When the report covers a single store its name goes in the
 * header; otherwise rows are grouped by store with a sub-header per group.
 * The category is shown only when the local preference
 * display_categories_on_items is 'YES' (resolved by the component), and the
 * item code is shown whenever the item has one. The component owns every
 * value; this builder only formats those same values as ESC/POS XML so the
 * USB receipt prints the same content as the browser print.
 */
export interface StockReportTicketData {
	rows: StockReportRow[];
	show_store: boolean;
	show_category: boolean;
	store_name: string;
	print_date: Date;
}

export class StockReportTicketBuilder {

	private data: StockReportTicketData;

	ticket_a_columns: number = 42;
	ticket_b_columns: number = 50;
	font_row: string = 'b';

	constructor(data: StockReportTicketData, ticket_a_columns: number = 42, ticket_b_columns: number = 50) {
		this.data = data;
		this.ticket_a_columns = ticket_a_columns;
		this.ticket_b_columns = ticket_b_columns;
	}

	private getEncoderMaxColumns(font_name: 'a' | 'b' = 'b'): number {
		return font_name == 'a' ? this.ticket_a_columns : this.ticket_b_columns;
	}

	private escapeXml(str: string): string {
		if (!str) return '';
		return str
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;')
			.replace(/'/g, '&apos;');
	}

	private formatShortDate(value: any): string {
		return Utils.getDateString(value, true);
	}

	private buildSectionTitle(title: string): string {
		return `<Text justification="center" bold="true">${this.escapeXml(title)}</Text>\n<Feed lines="1"></Feed>\n`;
	}

	public buildXml(): string {
		let xml = '<EscPos>\n';
		xml += '<Init></Init>\n';
		xml += this.buildHeader();
		xml += this.buildRows();
		xml += '<Feed lines="2"></Feed>\n';
		xml += '<Cut mode="partial"></Cut>\n';
		xml += '</EscPos>';
		return xml;
	}

	private buildHeader(): string {
		let xml = '';
		xml += this.buildSectionTitle('Inventario');
		xml += `<Text font="b" justification="left">Fecha: </Text><Text font="b" bold="true" justification="left">${this.escapeXml(this.formatShortDate(this.data.print_date))}</Text><Feed lines="1"></Feed>\n`;
		if (!this.data.show_store && this.data.store_name) {
			xml += `<Text font="b" justification="left">Sucursal: </Text><Text font="b" bold="true" justification="left">${this.escapeXml(this.data.store_name)}</Text><Feed lines="1"></Feed>\n`;
		}
		xml += '<Feed lines="1"></Feed>\n';
		return xml;
	}

	private formatItemName(row: StockReportRow): string {
		let name = '';

		if (this.data.show_category && row.categoria) {
			name = row.categoria + ' - ';
		}

		name += row.articulo || '';

		if (row.codigo) {
			name += ` (${row.codigo})`;
		}

		return name;
	}

	private groupRowsByStore(): { store: string, rows: StockReportRow[] }[] {
		const groups: { store: string, rows: StockReportRow[] }[] = [];
		for (const row of this.data.rows || []) {
			const store = row.sucursal || '';
			let group = groups.find(g => g.store === store);
			if (!group) {
				group = { store, rows: [] };
				groups.push(group);
			}
			group.rows.push(row);
		}
		return groups;
	}

	private buildRows(): string {
		const maxCols = this.getEncoderMaxColumns('b');
		const qtyWidth = 8;
		const articleWidth = Math.max(8, maxCols - qtyWidth - 1);

		let columnsXml = '<Columns>\n';
		columnsXml += `<Column width="${articleWidth}" align="left" marginRight="1"></Column>\n`;
		columnsXml += `<Column width="${qtyWidth}" align="right"></Column>\n`;
		columnsXml += '</Columns>\n';

		let rowsXml = '<Row>\n';
		rowsXml += '<Cell><Text bold="true">Articulo</Text></Cell>\n';
		rowsXml += '<Cell><Text bold="true">Cant</Text></Cell>\n';
		rowsXml += '</Row>\n';

		rowsXml += this.buildSeparatorRow();

		const groups = this.data.show_store
			? this.groupRowsByStore()
			: [{ store: '', rows: this.data.rows || [] }];

		for (const group of groups) {
			if (group.store) {
				rowsXml += '<Row>\n';
				rowsXml += `<Cell><Text bold="true">--- ${this.escapeXml(group.store)} ---</Text></Cell>\n`;
				rowsXml += '<Cell><Text></Text></Cell>\n';
				rowsXml += '</Row>\n';
			}
			for (const row of group.rows) {
				rowsXml += '<Row>\n';
				rowsXml += `<Cell><Text>${this.escapeXml(this.formatItemName(row))}</Text></Cell>\n`;
				rowsXml += `<Cell><Text>${row.cantidad ?? ''}</Text></Cell>\n`;
				rowsXml += '</Row>\n';
				rowsXml += this.buildSeparatorRow();
			}
		}

		return `<Table font="b">\n${columnsXml}<Rows>\n${rowsXml}</Rows>\n</Table>\n`;
	}

	private buildSeparatorRow(): string {
		let xml = '<Row>\n';
		xml += '<Cell><Rule/></Cell>\n';
		xml += '<Cell><Rule/></Cell>\n';
		xml += '</Row>\n';
		return xml;
	}
}
